import AddIcon from "@mui/icons-material/Add";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import MenuIcon from "@mui/icons-material/Menu";
import MinimizeIcon from "@mui/icons-material/Minimize";
import PeopleIcon from "@mui/icons-material/People";
import SendIcon from "@mui/icons-material/Send";
import {
	Badge,
	Box,
	Button,
	CircularProgress,
	Fab,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { AnimatePresence, motion } from "framer-motion";
import { type FC, useEffect, useRef, useState } from "react";
import Markdown from "react-markdown";
import { useAuth } from "@/contexts/AuthProvider";
import { useUserGuideChat } from "@/contexts/UserGuideChatContext";
import type { ChatMessage } from "@/utils/models";
import { InHouseTooltip } from "../InHouseTooltip/InHouseTooltip";
import { StableDemoAgent } from "../MockChatWidget/MockChatWidget";

const DRAWER_WIDTH = "40%";

const ChatWindow = styled(motion.div)(({ theme }) => ({
	position: "fixed",
	bottom: 100,
	right: 50,
	maxWidth: 600,
	width: "calc(100vw - 40px)",
	height: "min(650px, calc(100vh - 160px))",
	display: "flex",
	flexDirection: "row",
	borderRadius: theme.spacing(2),
	boxShadow: theme.shadows[8],
	zIndex: 1300,
	overflow: "hidden",
	backgroundColor: "#ffffff",
	border: "1px solid rgba(0, 0, 0, 0.1)",
	[theme.breakpoints.down("sm")]: { bottom: 0, left: 0, right: 0, maxWidth: "100%", width: "100%", height: "100vh", borderRadius: 0 },
}));

const SidebarDrawer = styled(Box)<{ open?: boolean }>(({ open }) => ({
	width: open ? DRAWER_WIDTH : 0,
	backgroundColor: "#f7f7f8",
	borderRight: open ? "1px solid rgba(0, 0, 0, 0.1)" : "none",
	overflow: "hidden",
	transition: "width 0.3s ease",
	display: "flex",
	flexDirection: "column",
	flexShrink: 0,
}));

const MessageContainer = styled(Box)(({ theme }) => ({
	flex: 1,
	padding: theme.spacing(2),
	overflowY: "auto",
	display: "flex",
	flexDirection: "column",
	gap: theme.spacing(1),
	"&::-webkit-scrollbar": {
		width: "8px",
	},
	"&::-webkit-scrollbar-track": {
		background: "transparent",
	},
	"&::-webkit-scrollbar-thumb": {
		background: "rgba(0, 0, 0, 0.2)",
		borderRadius: "4px",
	},
	"&::-webkit-scrollbar-thumb:hover": {
		background: "rgba(0, 0, 0, 0.3)",
	},
}));

const MessageBubble = styled(Box, {
	shouldForwardProp: (prop) => prop !== "isUser",
})<{ isUser: boolean }>(({ theme, isUser }) => ({
	padding: theme.spacing(2, 2.5),
	alignSelf: isUser ? "flex-end" : "flex-start",
	backgroundColor: isUser ? theme.palette.primary.main : "#f0f0f0",
	color: isUser ? "#ffffff" : "#333333",
	borderRadius: theme.spacing(2.5),
	borderBottomRightRadius: isUser ? theme.spacing(0.5) : theme.spacing(2.5),
	borderBottomLeftRadius: isUser ? theme.spacing(2.5) : theme.spacing(0.5),
	boxShadow: theme.shadows[1],
	fontSize: "16px",
	lineHeight: "1.6",
	width: "fit-content",
	maxWidth: "100%",
	"& strong": {
		fontWeight: 600,
		color: isUser ? "#ffffff" : "#000000",
	},
}));

const ThinkingIndicator = styled(Box)(() => ({
	display: "flex",
	alignItems: "center",
	gap: "8px",
	padding: "8px 12px",
	borderRadius: "8px",
	backgroundColor: "#f0f0f0",
	color: "#666666",
	fontSize: "14px",
}));

const StreamingMessage: FC<{ message: string; speed?: number }> = ({ message, speed = 20 }) => {
	const [displayedText, setDisplayedText] = useState<string>("");
	const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		setDisplayedText("");

		if (intervalRef.current) clearInterval(intervalRef.current);

		let charIndex = 0;

		intervalRef.current = setInterval(() => {
			if (charIndex < message.length) {
				setDisplayedText(message.substring(0, charIndex + 1));
				charIndex += 1;
			} else {
				if (intervalRef.current) {
					clearInterval(intervalRef.current);
					intervalRef.current = null;
				}
			}
		}, speed);

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
	}, [message, speed]);

	return <Markdown>{displayedText}</Markdown>;
};

// Dummy value for eligibleNursesCountForDemo, replace with actual logic if needed
const eligibleNursesCountForDemo = 0;

const RenderMessage: FC<{ isOpen: boolean; message: ChatMessage; chat: ReturnType<typeof useUserGuideChat>["chats"][number] }> = ({
	isOpen,
	message,
	chat,
}) => {
	const isUser = message.sender === "user";
	const shouldStream = isOpen && chat.streamingMessageId === message.id && !isUser;

	if (message.type === "text") {
		return (
			<Stack
				key={message.id}
				direction="row"
				spacing={1.5}
				sx={{
					alignItems: "flex-start",
					justifyContent: isUser ? "flex-end" : "flex-start",
					width: "100%",
					px: 1,
				}}
			>
				<Box sx={{ maxWidth: "85%" }}>
					<MessageBubble isUser={isUser}>
						{isUser ? message.text : shouldStream ? <StreamingMessage message={message.text} /> : <Markdown>{message.text}</Markdown>}
					</MessageBubble>
				</Box>
			</Stack>
		);
	}
	if (message.type === "task") {
		return (
			<Box sx={{ maxWidth: "85%", width: "100%" }}>
				<MessageBubble isUser={false} sx={{ width: "100%", maxWidth: "100%" }}>
					<Box sx={{ width: "100%" }}>
						<StableDemoAgent messageId={message.id} messageTimestamp={message.timestamp} eligibleNursesCount={eligibleNursesCountForDemo} />
					</Box>
				</MessageBubble>
			</Box>
		);
	}
};

export const UserGuideChatWidget: FC = () => {
	const {
		chats,
		allConversations,
		activeChat,
		activeChatId,
		isOpen,
		isDrawerOpen,
		messageEndRefs,
		updateChat,
		handleSendMessage,
		handleToggleChat,
		handleNewChat,
		handleSwitchChat,
		handleDeleteChat,
		setIsDrawerOpen,
		getChatTitle,
		isDeletingConversation,
		isLoadingAllConversations,
		isHiding,
		changeHideStatus,
	} = useUserGuideChat();

	const { userInfo } = useAuth();
	const [showAllConversations, setShowAllConversations] = useState<boolean>(false);
	const [isHoveringButton, setIsHoveringButton] = useState(false);

	const totalUnreadCount = chats.reduce((sum, chat) => sum + (chat.unreadCount || 0), 0);
	const badgeData =
		totalUnreadCount !== 0
			? { invisible: false, content: totalUnreadCount }
			: {
					content: (
						<CloseIcon
							sx={{ width: "14px" }}
							onClick={(e) => {
								e.stopPropagation();
								changeHideStatus(true);
							}}
						/>
					),
					invisible: !isHoveringButton,
				};

	const handleSelectExternalConversation = (sessionId: string) => {
		handleSwitchChat(sessionId);
		setShowAllConversations(false);
	};

	if (isHiding) return null;

	return (
		<>
			<InHouseTooltip title="Ask Annie">
				<Fab
					sx={{
						position: "fixed",
						bottom: 40,
						right: 50,
						zIndex: 1300,
						boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
						bgcolor: "primary.main",
						color: "primary.contrastText",
						width: "48px",
						height: "48px",
						"&:hover": { bgcolor: "primary.dark" },
					}}
					color="primary"
					onClick={handleToggleChat}
					aria-label="user guide chat"
					onMouseEnter={() => setIsHoveringButton(true)}
					onMouseLeave={() => setIsHoveringButton(false)}
				>
					<Badge badgeContent={badgeData.content} color="error" invisible={badgeData.invisible}>
						<img src="/chat.svg" alt="chat" style={{ width: "48px", height: "48px" }} />
					</Badge>
				</Fab>
			</InHouseTooltip>
			<AnimatePresence>
				{isOpen && activeChat && (
					<ChatWindow
						initial={{ opacity: 0, scale: 0.8, y: 20 }}
						animate={{ opacity: 1, scale: 1, y: 0 }}
						exit={{ opacity: 0, scale: 0.8, y: 20 }}
						transition={{ duration: 0.2, ease: "easeOut" }}
					>
						<SidebarDrawer open={isDrawerOpen}>
							<Box sx={{ p: 2, borderBottom: "1px solid rgba(0, 0, 0, 0.1)" }}>
								<Stack spacing={1}>
									<Box
										onClick={handleNewChat}
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 1,
											padding: "12px",
											borderRadius: "8px",
											cursor: "pointer",
											border: "1px solid rgba(0, 0, 0, 0.1)",
											"&:hover": {
												backgroundColor: "rgba(0, 0, 0, 0.05)",
											},
										}}
									>
										<AddIcon sx={{ color: "#333333", fontSize: 20 }} />
										<Typography sx={{ color: "#333333", fontSize: "14px", fontWeight: 500 }}>New chat</Typography>
									</Box>
									{userInfo?.support && (
										<Button
											variant="outlined"
											size="small"
											startIcon={<PeopleIcon />}
											onClick={() => setShowAllConversations(!showAllConversations)}
											sx={{ fontSize: "12px" }}
										>
											{showAllConversations ? "My Chats" : "All Conversations"}
										</Button>
									)}
								</Stack>
							</Box>
							<List sx={{ flex: 1, overflowY: "auto", p: 0 }}>
								{showAllConversations && userInfo?.support ? (
									isLoadingAllConversations ? (
										<Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
											<CircularProgress size={24} />
										</Box>
									) : (
										allConversations?.map((conv) => {
											const firstUserMessage = conv.messages.find((m) => m.sender === "user");
											const displayText = firstUserMessage?.text || "No messages";
											return (
												<ListItem key={conv.id} disablePadding>
													<ListItemButton onClick={() => handleSelectExternalConversation(conv.id)}>
														<ListItemText
															primary={
																<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
																	<ChatIcon sx={{ fontSize: 16, color: "rgba(0, 0, 0, 0.6)" }} />
																	<Typography sx={{ fontSize: "14px", color: "#333333" }}>{displayText.substring(0, 50)}...</Typography>
																</Box>
															}
															secondary={<Typography sx={{ fontSize: "12px", color: "rgba(0, 0, 0, 0.6)" }}>Session: {conv.id.substring(0, 8)}</Typography>}
														/>
													</ListItemButton>
												</ListItem>
											);
										})
									)
								) : (
									chats.map((chat) => (
										<ListItem
											key={chat.id}
											disablePadding
											secondaryAction={
												chats.length > 1 ? (
													<IconButton
														edge="end"
														onClick={(e) => handleDeleteChat(chat.id, e)}
														sx={{ color: "rgba(0, 0, 0, 0.5)" }}
														disabled={isDeletingConversation}
													>
														{isDeletingConversation ? <CircularProgress size={20} /> : <DeleteIcon fontSize="small" />}
													</IconButton>
												) : null
											}
										>
											<ListItemButton
												selected={chat.id === activeChatId}
												onClick={() => handleSwitchChat(chat.id)}
												sx={{
													"&.Mui-selected": { bgcolor: "rgba(0, 0, 0, 0.08)" },
													"&:hover": { bgcolor: "rgba(0, 0, 0, 0.05)" },
												}}
											>
												<ListItemText
													primary={
														<Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
															<ChatIcon sx={{ fontSize: 16, color: "rgba(0, 0, 0, 0.6)", flexShrink: 0 }} />
															<Tooltip title={getChatTitle(chat)} placement="right" arrow>
																<Typography
																	sx={{
																		color: "#333333",
																		fontSize: "14px",
																		fontWeight: chat.id === activeChatId ? 600 : 400,
																		overflow: "hidden",
																		textOverflow: "ellipsis",
																		whiteSpace: "nowrap",
																		flex: 1,
																		minWidth: 0,
																	}}
																>
																	{getChatTitle(chat)}
																</Typography>
															</Tooltip>
															{!!chat.unreadCount && (
																<Badge badgeContent={chat.unreadCount} color="error" sx={{ flexShrink: 0 }}>
																	<Box />
																</Badge>
															)}
														</Box>
													}
												/>
											</ListItemButton>
										</ListItem>
									))
								)}
							</List>
						</SidebarDrawer>

						<Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									p: 2,
									borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
									backgroundColor: "#f7f7f8",
								}}
							>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0, position: "relative" }}>
									<IconButton onClick={() => setIsDrawerOpen(!isDrawerOpen)} sx={{ color: "#333333", flexShrink: 0 }}>
										<MenuIcon />
									</IconButton>
									{/* Beta badge at 45 degrees */}
									<Box
										sx={{
											position: "relative",
											display: "flex",
											alignItems: "center",
											mr: 1,
										}}
									>
										<Box
											sx={{
												position: "absolute",
												left: -16,
												top: -30,
												transform: "rotate(-45deg)",
												bgcolor: "#ff1744",
												color: "#fff",
												fontSize: "10px",
												fontWeight: 600,
												px: 1.25,
												py: 0.25,
												boxShadow: 2,
												pointerEvents: "none",
												zIndex: 1,
												borderRadius: "6px",
												// position: "absolute",
												"::before": {
													content: '""',
													position: "absolute",
													top: -2,
													left: -2,
													right: -2,
													bottom: -2,
													borderRadius: "8px",
													background: "linear-gradient(90deg, #ff69b4 0%, #b266ff 100%)",
													zIndex: -1,
													WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
													WebkitMaskComposite: "xor",
													maskComposite: "exclude",
												},
												border: "2px solid transparent",
												backgroundClip: "padding-box",
											}}
										>
											BETA
										</Box>
									</Box>
									<Tooltip title={getChatTitle(activeChat)} placement="bottom" arrow>
										<Typography
											sx={{
												color: "#333333",
												fontSize: "16px",
												fontWeight: 600,
												flex: 1,
												minWidth: 0,
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}
										>
											{getChatTitle(activeChat)}
										</Typography>
									</Tooltip>
									{!!activeChat.unreadCount && (
										<Badge badgeContent={activeChat.unreadCount} color="error" sx={{ flexShrink: 0 }}>
											<Box />
										</Badge>
									)}
								</Box>
								<Stack direction="row" spacing={1}>
									<Box
										onClick={handleNewChat}
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 0.5,
											padding: "6px 12px",
											borderRadius: "6px",
											cursor: "pointer",
											"&:hover": {
												backgroundColor: "rgba(0, 0, 0, 0.05)",
											},
										}}
									>
										<AddIcon sx={{ color: "#333333", fontSize: 18 }} />
										<Typography sx={{ color: "#333333", fontSize: "14px" }}>New</Typography>
									</Box>
									<InHouseTooltip title="Minimize chat">
										<IconButton onClick={handleToggleChat} sx={{ color: "#333333" }}>
											<MinimizeIcon />
										</IconButton>
									</InHouseTooltip>
								</Stack>
							</Box>

							<MessageContainer key={activeChatId}>
								{activeChat.messages.map((message) => (
									<RenderMessage key={message.id} isOpen={isOpen} message={message} chat={activeChat} />
								))}

								{activeChat.isThinking && isOpen && (
									<Stack
										direction="row"
										spacing={1.5}
										sx={{
											alignItems: "center",
											justifyContent: "flex-start",
											width: "100%",
											px: 1,
										}}
									>
										<ThinkingIndicator>
											<Typography variant="caption" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
												Thinking
												<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "2px", ml: 0.5, height: "14px", overflow: "hidden" }}>
													{[0, 1, 2].map((dotIndex) => (
														<Box
															key={`thinking-dot-${dotIndex}`}
															component="span"
															sx={{
																display: "inline-block",
																width: "4px",
																height: "4px",
																borderRadius: "50%",
																backgroundColor: "#666666",
																animation: `dotPop 1.2s infinite ${dotIndex * 0.2}s`,
																"@keyframes dotPop": {
																	"0%": {
																		transform: "translateY(-7px)",
																		opacity: 0,
																	},
																	"50%": {
																		transform: "translateY(0)",
																		opacity: 1,
																	},
																	"100%": {
																		transform: "translateY(0)",
																		opacity: 1,
																	},
																},
															}}
														/>
													))}
												</Box>
											</Typography>
										</ThinkingIndicator>
									</Stack>
								)}
								<div
									ref={(el) => {
										messageEndRefs.current[activeChat.id] = el;
									}}
								/>
							</MessageContainer>

							<Box
								component="form"
								onSubmit={handleSendMessage(activeChat.id)}
								sx={{ p: 2, borderTop: "1px solid rgba(0, 0, 0, 0.1)", backgroundColor: "#ffffff", display: "flex", alignItems: "center" }}
							>
								<TextField
									fullWidth
									variant="outlined"
									placeholder="Ask me anything about InHouse..."
									value={activeChat.inputMessage}
									onChange={(e) => updateChat(activeChat.id, { inputMessage: e.target.value })}
									disabled={activeChat.isThinking || activeChat.isTyping}
									size="small"
									sx={{
										mr: 1,
										"& .MuiOutlinedInput-root": {
											backgroundColor: "#f0f0f0",
											color: "#333333",
											"& fieldset": {
												borderColor: "rgba(0, 0, 0, 0.2)",
											},
											"&:hover fieldset": {
												borderColor: "rgba(0, 0, 0, 0.3)",
											},
											"&.Mui-focused fieldset": {
												borderColor: "primary.main",
											},
											"& .MuiInputBase-input": {
												color: "#333333",
											},
										},
										"& .MuiInputBase-input::placeholder": {
											color: "rgba(0, 0, 0, 0.5)",
										},
									}}
								/>
								<IconButton
									type="submit"
									color="primary"
									disabled={activeChat.inputMessage.trim() === "" || activeChat.isThinking || activeChat.isTyping}
									sx={{ ml: 1, color: "primary.main" }}
								>
									<SendIcon />
								</IconButton>
							</Box>
						</Box>
					</ChatWindow>
				)}
			</AnimatePresence>
		</>
	);
};
